import React, { useMemo } from "react";

import { useProjectPageContext } from "./ProjectPageContext";
import ConcretePoursChart from "./metrics/charts/ConcretePoursChart";
import WorkerHoursChart from "./metrics/charts/WorkerHoursChart";
import {
  buildConcretePoursOverviewUi,
  buildWorkerHoursOverviewUi,
} from "./metrics/metricsHelpers";
import ProjectSummaryCard from "./metrics/ProjectSummaryCard";
import ProjectTicketSummaries from "./ProjectTicketSummaries";

/**
 * Render the project overview tab MVP content.
 *
 * @returns {JSX.Element}
 */
const ProjectOverview = () => {
  const {
    placeholderContent,
    workerHoursSummary,
    concretePoursSummary,
    setWorkerHoursRangeSelection,
    setWorkerHoursTypeKeys,
    setConcretePoursRangeSelection,
    setConcretePoursTypeKey,
  } = useProjectPageContext();
  const { overview } = placeholderContent;

  const workerHoursUi = useMemo(
    () => buildWorkerHoursOverviewUi(workerHoursSummary),
    [workerHoursSummary]
  );

  const concretePoursUi = useMemo(
    () => buildConcretePoursOverviewUi(concretePoursSummary),
    [concretePoursSummary]
  );

  return (
    <div className="space-y-2">
      <ProjectSummaryCard
        title={overview.workerHours.title}
        subtitle={overview.workerHours.subtitle}
        metrics={workerHoursSummary.metrics}
        error={workerHoursSummary.error}
        typeKeys={workerHoursUi.typeKeys}
        onTypeChange={setWorkerHoursTypeKeys}
        typeOptions={workerHoursUi.typeOptions}
        typeAriaLabel="Worker hours types"
        range={workerHoursUi.range}
        onRangeChange={setWorkerHoursRangeSelection}
        startSuggestions={workerHoursUi.startSuggestions}
        rangeAriaLabel="Worker hours date range"
        chartDays={workerHoursSummary.data?.days}
        chart={
          <WorkerHoursChart
            days={workerHoursSummary.data?.days}
            range={workerHoursSummary.range}
            typeLabel={workerHoursUi.chartTypeLabel}
            isLoading={workerHoursSummary.isLoading}
          />
        }
      />

      <ProjectSummaryCard
        title={overview.concretePours.title}
        subtitle={overview.concretePours.subtitle}
        metrics={concretePoursSummary.metrics}
        error={concretePoursSummary.error}
        typeKey={concretePoursUi.typeKey}
        onTypeChange={setConcretePoursTypeKey}
        typeOptions={concretePoursUi.typeOptions}
        typeAriaLabel="Concrete pours type"
        range={concretePoursUi.range}
        onRangeChange={setConcretePoursRangeSelection}
        startSuggestions={concretePoursUi.startSuggestions}
        rangeAriaLabel="Concrete pours date range"
        chartDays={concretePoursSummary.chartDays}
        chart={
          <ConcretePoursChart
            days={concretePoursSummary.chartDays}
            range={concretePoursSummary.range}
            typeLabel={concretePoursUi.typeLabel}
            isLoading={concretePoursSummary.isLoading}
          />
        }
      />

      <ProjectTicketSummaries />
    </div>
  );
};

export default ProjectOverview;
